import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";

/**
 * The visual card shown by {@link confirmLeave}. Mirrors the in-modal overlay in
 * ResponsiveModal so every "discard unsaved changes?" prompt across the app looks
 * identical — whether the modal is ResponsiveModal-based or hand-rolled.
 *
 * Default action is the SAFE one (Stay): ESC and backdrop both cancel; there is
 * deliberately no Enter→Leave binding so a stray keypress can't discard work.
 */
function LeaveConfirmCard({ message, onStay, onLeave }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onStay();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onStay]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onStay();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Unsaved changes"
        className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-800">Unsaved changes</h3>
        <p className="text-sm text-gray-600 mt-2">{message}</p>
        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onStay}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
          >
            Stay
          </button>
          <button
            type="button"
            onClick={onLeave}
            className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 text-sm font-medium"
          >
            Leave anyway
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Imperative "leave without saving?" confirmation. Renders the styled card into
 * a detached container and resolves true (Leave / discard) or false (Stay).
 * Usable from anywhere — event handlers or plain code — with no host component
 * required. Returns a Promise, so call it as `if (await confirmLeave()) ...` or
 * `confirmLeave().then((ok) => ok && onClose())`.
 */
export function confirmLeave(
  message = "You have unsaved changes. Leave without saving? Your changes will be lost."
) {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);
    let settled = false;
    const close = (result) => {
      if (settled) return;
      settled = true;
      // Defer unmount so React isn't torn down from inside its own event handler.
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, 0);
      resolve(result);
    };
    root.render(
      <LeaveConfirmCard
        message={message}
        onStay={() => close(false)}
        onLeave={() => close(true)}
      />
    );
  });
}
